import React, { useEffect, useState } from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";
import JsBarcode from "jsbarcode";
import {
  getChargeTypeCode,
  getCachedOtherChargeOptions,
  loadOtherChargeOptions,
} from "../utils/otherChargeOptions";
import { getTenantNameDisplay } from "../utils/tenantName";

// Register fonts if needed
Font.register({
  family: "Times New Roman",
  src: "https://db.onlinewebfonts.com/t/32441506567156636049eb850b53f02a.ttf",
});

// Conversion constant: 1mm = 2.834645669pt
const mmToPt = (mm) => mm * 2.834645669;

// Create styles for barcode sheet (A4: 210mm × 297mm)
const styles = StyleSheet.create({
  page: {
    fontFamily: "Times-Roman",
  },
  pageGold: {
    fontFamily: "Times-Roman",
  },
  container: {
    flex: 1,
  },
  header: {
    alignItems: "center",
    marginBottom: 15,
  },
  title: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#333",
    marginBottom: 5,
  },
  subtitle: {
    fontSize: 10,
    color: "#666",
    textAlign: "center",
  },
  // Tags on A4: shifted right + slightly less top margin (match label sheet position)
  squareGrid: {
    paddingTop: mmToPt(9),
    paddingLeft: mmToPt(55),
    paddingBottom: mmToPt(5),
    paddingRight: 0,
  },
  squareGridGold: {
    paddingTop: mmToPt(7.5),
    paddingLeft: mmToPt(55),
    paddingBottom: mmToPt(7),
    paddingRight: 0,
  },
  // Main square container Silver (55mm x 14mm)
  squareContainer: {
    width: mmToPt(55), // 155.91pt
    height: mmToPt(14), // 39.69pt
    // border: "0.1pt solid #7d6c6c",
    // borderRadius: 8,
    marginBottom: mmToPt(2.5), // 7.09pt (gap between squares)
    flexDirection: "row",
    overflow: "hidden",
  },
  // Gold: 55mm x 18mm, no marginBottom
  squareContainerGold: {
    width: mmToPt(55),
    height: mmToPt(18),
    // border: "0.1pt solid #7d6c6c",
    // borderRadius: 8,
    flexDirection: "row",
    overflow: "hidden",
  },
  // Left half (27.5mm) - Barcode area (Silver)
  leftHalf: {
    width: mmToPt(27.5), // 77.95pt
    height: "100%",
    // borderTopLeftRadius: 6,
    // borderBottomLeftRadius: 6,
    flexDirection: "column",
    justifyContent: "flex-start",
    paddingTop: 2,
    paddingHorizontal: 1,
  },
  // Gold: increased top padding
  leftHalfGold: {
    width: mmToPt(27.5),
    height: "100%",
    // borderTopLeftRadius: 6,
    // borderBottomLeftRadius: 6,
    flexDirection: "column",
    justifyContent: "flex-start",
    paddingTop: 4,
    paddingHorizontal: 1,
  },
  // Black partition line
  partitionLine: {
    width: 0.1,
    height: "100%",
    // backgroundColor: "#000000",
  },
  // Right half (27.5mm) - Data area (Silver)
  rightHalf: {
    width: mmToPt(27.5), // 77.95pt
    height: "100%",
    // borderTopRightRadius: 6,
    // borderBottomRightRadius: 6,
    flexDirection: "column",
    justifyContent: "flex-start",
    paddingTop: 2,
    paddingHorizontal: 1,
    alignItems: "center",
  },
  // Gold: increased top padding
  rightHalfGold: {
    width: mmToPt(27.5),
    height: "100%",
    // borderTopRightRadius: 6,
    // borderBottomRightRadius: 6,
    flexDirection: "column",
    justifyContent: "flex-start",
    paddingTop: 4,
    paddingHorizontal: 1,
    alignItems: "center",
  },
  // Fixed width container for alignment - labels sit left in the right half
  alignContainer: {
    width: 70,
    alignSelf: "center",
    paddingLeft: 10, // Labels shifted left
  },
  // Row layout for label-value pairs in columns
  weightRow: {
    flexDirection: "row",
    marginBottom: 0.9,
  },
  // Label with fixed width for alignment
  weightLabel: {
    width: 18, // Fixed width for labels to align in column
  },
  // Value with margin for spacing
  weightValue: {
    marginLeft: 8, // Wider gap between label (GW/LW/NW/…) and value
  },
  // Silver right half: value column (GW, NW, size) fixed width, right-aligned like Gold
  weightValueSilver: {
    width: 18,
    textAlign: "left",
  },
  // Text styles – headings (MANGLIK, YAMUNA ERP). manglikText = Silver "YAMUNA ERP"
  manglikText: {
    fontSize: 6.5,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 1,
    color: "#000",
  },
  manglikTextGold: {
    fontSize: 6,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 1,
    color: "#000",
  },
  tagText: {
    fontSize: 7,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 1,
    color: "#000",
  },
  tagTextGold: {
    fontSize: 6.5,
    fontWeight: "bold",
    textAlign: "center",
    // marginBottom: 1,
    color: "#000",
  },
  // Row below barcode: same width as barcode (80%), tag_no left aligns with barcode start, % right aligns with barcode end
  tagLbrRow: {
    width: "80%",
    alignSelf: "center",
    flexDirection: "row",
    justifyContent: "space-between",
    // marginBottom: 1,
  },
  chargesText: {
    fontSize: 5,
    fontWeight: "bold",
    textAlign: "center",
    color: "#000",
    lineHeight: 0.8,
  },
  chargesTextGold: {
    fontSize: 5,
    fontWeight: "bold",
    textAlign: "center",
    color: "#000",
    lineHeight: 0.8,
  },
  rightSideText: {
    fontSize: 7,
    fontWeight: "bold",
    marginBottom: 0.5,
    color: "#000",
    lineHeight: 0.8,
  },
  rightSideTextGold: {
    fontSize: 6.5,
    fontWeight: "bold",
    marginBottom: 0.5,
    color: "#000",
    lineHeight: 0.8,
  },
  rightSideSmallText: {
    fontSize: 6.5,
    fontWeight: "bold",
    marginBottom: 0.5,
    color: "#000",
    lineHeight: 0.8,
  },
  rightSideSmallTextGold: {
    fontSize: 6.5,
    fontWeight: "bold",
    marginBottom: 0.5,
    color: "#000",
    lineHeight: 0.8,
  },
  // Barcode image styling
  barcodeImage: {
    width: "90%",
    height: 10,
    marginBottom: 1,
  },
  barcodeText: {
    fontSize: 6,
    fontWeight: "bold",
    color: "#000",
    textAlign: "center",
  },
  footer: {
    position: "absolute",
    bottom: 10,
    left: 10,
    right: 10,
    textAlign: "center",
    fontSize: 8,
    color: "#999",
  },
});

/** Normalize list row / show API product into barcode label fields */
function normalizeBarcodeProduct(raw) {
  if (!raw) return {};

  const gwtRaw = raw.gwt ?? raw.GWT ?? raw.gWt ?? raw.current_gwt ?? "";
  const lwtRaw =
    raw.lwt_customer ??
    raw.LWt_customer ??
    raw.LWT_customer ??
    raw.lWtCustomer ??
    raw.lWt ??
    "";
  const nwtRaw = raw.nwt ?? raw.NWT ?? "";

  const gwtNum = parseFloat(gwtRaw);
  const lwtNum = parseFloat(lwtRaw);
  const nwtNum = parseFloat(nwtRaw);

  let NWt = null;
  if (!Number.isNaN(nwtNum)) {
    NWt = nwtNum;
  } else if (!Number.isNaN(gwtNum)) {
    NWt = Number.isNaN(lwtNum) ? gwtNum : gwtNum - lwtNum;
  }

  return {
    tag_no: raw.tag_no ?? raw.tagNo ?? "",
    GWT: Number.isNaN(gwtNum) ? null : gwtNum,
    LWt_customer: Number.isNaN(lwtNum) ? null : lwtNum,
    NWt,
    short_code:
      raw.item_category_short_code ??
      raw.short_code ??
      raw.shortCode ??
      "",
    supplier_short_code:
      raw.supplier_short_code ?? raw.supplier?.short_code ?? "",
    size: raw.size ?? "",
    touch:
      raw.metal_group_carat ??
      raw.touch ??
      raw.purity ??
      raw.Touch ??
      "",
    huid: raw.huid ?? "",
    lbr_type: raw.lbr_type ?? raw.LBR_type ?? raw.lType ?? "",
    lbr_percent: raw.lbr_percent ?? raw.lbrPercentage ?? raw.LBR ?? "",
    lbr_rate: raw.lbr_rate ?? raw.lbrRate ?? raw.LBR_rate ?? "",
    lbr_amt: raw.lbr_amt ?? raw.lbrAmt ?? raw.LBR_amt ?? "",
    charges: Array.isArray(raw.charges) ? raw.charges : [],
  };
}

/** True when labour field has a usable non-zero value */
const hasLbrValue = (v) => {
  if (v == null || v === "") return false;
  const n = Number(v);
  return Number.isFinite(n) && n !== 0;
};

/**
 * Silver/Gold barcode LBR label:
 * - NWt / GWt / Pcs → lbr_percent if set, else lbr_rate
 * - Fixed → lbr_amt
 */
const formatBarcodeLbrLabel = (productData) => {
  const lbrType = String(productData?.lbr_type || "")
    .trim()
    .toLowerCase();
  const percent = productData?.lbr_percent;
  const rate = productData?.lbr_rate;
  const amt = productData?.lbr_amt;

  if (lbrType === "fixed") {
    return hasLbrValue(amt) ? String(Number(amt)) : "";
  }

  if (lbrType === "nwt" || lbrType === "gwt" || lbrType === "pcs") {
    if (hasLbrValue(percent)) return `${Number(percent)}%`;
    if (hasLbrValue(rate)) return String(Number(rate));
    return "";
  }

  // Fallback when type is missing
  if (hasLbrValue(percent)) return `${Number(percent)}%`;
  if (hasLbrValue(rate)) return String(Number(rate));
  if (hasLbrValue(amt)) return String(Number(amt));
  return "";
};

const formatWeight = (value) => {
  const num = parseFloat(value);
  return Number.isFinite(num) ? num.toFixed(3) : "";
};

/** Barcode charge line: `CODE/pcsxrate=total` (same for Gold & Silver tags) */
const formatBarcodeChargeLine = (charge, chargeOptions) => {
  const code = getChargeTypeCode(charge.charge_type, chargeOptions);
  const pcs = charge.pcs != null ? Number(charge.pcs) : 1;
  const rate = charge.rate != null ? Number(charge.rate) : 0;
  const total = charge.amount != null ? Number(charge.amount) : 0;
  return [code, `${pcs}x${rate}=${total}`].filter(Boolean).join("/");
};

/** Normalize HUID for display (## → /) */
const formatHuidDisplay = (huid) => {
  if (!huid || huid === "N/A") return "";
  return String(huid).replace(/##/g, "/");
};

/** Shrink HUID font as character count grows so it fits the right half */
const getHuidFontSize = (huidText) => {
  const len = String(huidText || "").length;
  if (len <= 8) return 6.5;
  if (len <= 12) return 5.5;
  if (len <= 16) return 4.5;
  if (len <= 20) return 3.8;
  if (len <= 24) return 3.3;
  return 2.8;
};

// Function to generate barcode as base64 image
const generateBarcodeImage = (value) => {
  try {
    const canvas = document.createElement("canvas");
    JsBarcode(canvas, value, {
      format: "CODE128",
      width: 1,
      height: 25,
      displayValue: false,
      fontSize: 8,
      fontOptions: "bold",
      margin: 0,
      background: "transparent",
    });
    return canvas.toDataURL("image/png");
  } catch (error) {
    console.error("Error generating barcode:", error);
    return null;
  }
};

/**
 * Build sheet pages for Gold/Silver barcode labels.
 * Start-position blanks apply to the FIRST sheet only; later sheets fill from slot 1.
 */
export function buildBarcodeSheetPages(
  selectedItems,
  barcodeType,
  startPosition = 1,
) {
  const items = Array.isArray(selectedItems) ? selectedItems : [];
  const slotsPerPage =
    barcodeType === "Silver" ? 10 : barcodeType === "Gold" ? 9 : null;

  if (!slotsPerPage) {
    return [items.length ? [...items] : [null]];
  }

  const start = Math.max(
    1,
    Math.min(Number(startPosition) || 1, slotsPerPage),
  );
  const firstPageSkip = start - 1;
  const pages = [];
  let itemIndex = 0;

  const firstPage = [];
  for (let i = 0; i < slotsPerPage; i++) {
    if (i < firstPageSkip) {
      firstPage.push(null);
    } else if (itemIndex < items.length) {
      firstPage.push(items[itemIndex++]);
    } else {
      firstPage.push(null);
    }
  }
  pages.push(firstPage);

  while (itemIndex < items.length) {
    const page = [];
    for (let i = 0; i < slotsPerPage; i++) {
      page.push(itemIndex < items.length ? items[itemIndex++] : null);
    }
    pages.push(page);
  }

  return pages;
}

// Barcode PDF component
const BarcodePDF = ({
  selectedItems,
  barcodeType = "Silver",
  startPosition = 1,
}) => {
  const [chargeOptions, setChargeOptions] = useState(getCachedOtherChargeOptions);
  useEffect(() => {
    let cancelled = false;
    loadOtherChargeOptions()
      .then((rows) => {
        if (!cancelled) setChargeOptions(rows);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  const tenantNameDisplay = getTenantNameDisplay();
  // Silver: 10 squares; Gold: 9 squares
  const slotsPerPage =
    barcodeType === "Silver"
      ? 10
      : barcodeType === "Gold"
        ? 9
        : selectedItems.length || 1;

  const pages = buildBarcodeSheetPages(
    selectedItems,
    barcodeType,
    startPosition,
  );

  const isGold = barcodeType === "Gold";
  const gridStyle = isGold ? styles.squareGridGold : styles.squareGrid;
  const containerStyle = isGold
    ? styles.squareContainerGold
    : styles.squareContainer;
  const pageStyle = isGold ? styles.pageGold : styles.page;
  const leftHalfStyle = isGold ? styles.leftHalfGold : styles.leftHalf;
  const rightHalfStyle = isGold ? styles.rightHalfGold : styles.rightHalf;
  const manglikStyle = isGold ? styles.manglikTextGold : styles.manglikText;
  const tagStyle = isGold ? styles.tagTextGold : styles.tagText;
  const chargesStyle = isGold ? styles.chargesTextGold : styles.chargesText;
  const rightTextStyle = isGold
    ? styles.rightSideTextGold
    : styles.rightSideText;
  const rightSmallStyle = isGold
    ? styles.rightSideSmallTextGold
    : styles.rightSideSmallText;

  const renderSquare = (item, index, pageIndex) => {
    const productData = normalizeBarcodeProduct(
      item?.barcodeData?.product || item,
    );
    const barcodeValue =
      productData.tag_no ||
      item?.tagNo ||
      item?.id ||
      `ITEM${pageIndex + 1}-${index + 1}`;
    const barcodeImage = item ? generateBarcodeImage(barcodeValue) : null;

    return (
      <View
        key={`square-${pageIndex}-${index}`}
        style={[
          containerStyle,
          // Silver only: remove margin-bottom from last square
          !isGold && index === slotsPerPage - 1 && { marginBottom: 0 },
        ]}
      >
        {/* Left Half - Barcode Area */}
        <View style={leftHalfStyle}>
          {item ? (
            <>
              {isGold && <Text style={manglikStyle}>MANGLIK</Text>}

              {/* Barcode */}
              {barcodeImage && (
                <Image
                  src={barcodeImage}
                  style={{
                    width: "80%",
                    height: isGold ? 8 : 6,
                    marginBottom: 1,
                    alignSelf: "center",
                  }}
                />
              )}

              {/* Tag no (left) | LBR (right): % or rate for NWt/GWt/Pcs, amt for Fixed */}
              <View style={styles.tagLbrRow}>
                <Text style={[tagStyle, { textAlign: "left" }]}>
                  {barcodeValue}
                </Text>
                <Text style={[tagStyle, { textAlign: "right" }]}>
                  {formatBarcodeLbrLabel(productData)}
                </Text>
              </View>

              {isGold ? (
                /* Gold: Charges: ShortCode/pcs x rate = total */
                <View style={{ flex: 1, justifyContent: "flex-start" }}>
                  {productData?.charges?.length > 0 &&
                    productData.charges.slice(0, 4).map((charge, chargeIndex) => (
                      <Text key={chargeIndex} style={chargesStyle}>
                        {formatBarcodeChargeLine(charge, chargeOptions)}
                      </Text>
                    ))}
                </View>
              ) : (
                /* Silver: short_code + other charges (same format as gold) */
                <View style={{ flex: 1, justifyContent: "flex-start" }}>
                  <View style={styles.tagLbrRow}>
                    <Text style={[tagStyle, { textAlign: "left" }]}>
                      {productData?.short_code || ""}
                    </Text>
                  </View>
                  {productData?.charges?.length > 0 &&
                    productData.charges.slice(0, 3).map((charge, chargeIndex) => (
                      <Text key={chargeIndex} style={chargesStyle}>
                        {formatBarcodeChargeLine(charge, chargeOptions)}
                      </Text>
                    ))}
                </View>
              )}
            </>
          ) : null}
        </View>

        {/* Black Partition Line */}
        <View style={styles.partitionLine} />

        {/* Right Half - Product Data */}
        <View style={rightHalfStyle}>
          {item ? (
            <>
              {isGold ? (
                <>
                  <View style={styles.alignContainer}>
                    {/* GW */}
                    <View style={styles.weightRow}>
                      <Text style={[rightTextStyle, styles.weightLabel]}>
                        GW
                      </Text>
                      <Text style={[rightTextStyle, styles.weightValue]}>
                        {formatWeight(productData.GWT)}
                      </Text>
                    </View>

                    {/* LW */}
                    <View style={styles.weightRow}>
                      <Text style={[rightTextStyle, styles.weightLabel]}>
                        LW
                      </Text>
                      <Text style={[rightTextStyle, styles.weightValue]}>
                        {productData.LWt_customer
                          ? formatWeight(productData.LWt_customer)
                          : ""}
                      </Text>
                    </View>

                    {/* NW = GW - LW */}
                    <View style={styles.weightRow}>
                      <Text style={[rightTextStyle, styles.weightLabel]}>
                        NW
                      </Text>
                      <Text style={[rightTextStyle, styles.weightValue]}>
                        {formatWeight(productData.NWt)}
                      </Text>
                    </View>

                    {/* Supplier short code and size (one line) */}
                    <View style={styles.weightRow}>
                      <Text style={[rightSmallStyle, styles.weightLabel]}>
                        {productData?.supplier_short_code || ""}
                      </Text>
                      <Text style={[rightSmallStyle, styles.weightValue]}>
                        {productData?.size ?? ""}
                      </Text>
                    </View>

                    {/* Short code and touch (one line) */}
                    <View style={styles.weightRow}>
                      <Text style={[rightTextStyle, styles.weightLabel]}>
                        {productData?.short_code || ""}
                      </Text>
                      <Text style={[rightTextStyle, styles.weightValue]}>
                        {(() => {
                          const touchValue = productData?.touch;
                          if (
                            touchValue === null ||
                            touchValue === undefined ||
                            touchValue === ""
                          ) {
                            return "";
                          }
                          const num = parseFloat(touchValue);
                          // Show 22c / 18c (not 22.00)
                          if (Number.isFinite(num)) {
                            return `${num}c`;
                          }
                          const raw = String(touchValue).trim();
                          return /c$/i.test(raw) ? raw : `${raw}c`;
                        })()}
                      </Text>
                    </View>
                  </View>

                  {/* HUID - new line, centred; font shrinks when longer */}
                  {(() => {
                    const huidText = formatHuidDisplay(productData?.huid);
                    if (!huidText) return null;
                    return (
                      <View
                        style={{
                          alignSelf: "stretch",
                          marginTop: 1,
                          alignItems: "center",
                          paddingHorizontal: 1,
                        }}
                      >
                        <Text
                          style={[
                            rightSmallStyle,
                            {
                              textAlign: "center",
                              fontSize: getHuidFontSize(huidText),
                              marginBottom: 0,
                            },
                          ]}
                        >
                          {huidText}
                        </Text>
                      </View>
                    );
                  })()}
                </>
              ) : (
                /* Silver: YAMUNA ERP, GW/NW, supplier short code + size */
                <View
                  style={{
                    alignSelf: "center",
                    alignItems: "center",
                    width: 52,
                  }}
                >
                  <Text
                    style={[
                      manglikStyle,
                      { marginBottom: 2, alignSelf: "center" },
                    ]}
                  >
                    {tenantNameDisplay}
                  </Text>
                  <View
                    style={[
                      styles.weightRow,
                      { width: 38, justifyContent: "space-between" },
                    ]}
                  >
                    <Text style={[rightTextStyle, styles.weightLabel]}>GW</Text>
                    <Text
                      style={[rightTextStyle, styles.weightValueSilver]}
                    >
                      {formatWeight(productData.GWT)}
                    </Text>
                  </View>
                  <View
                    style={[
                      styles.weightRow,
                      { width: 38, justifyContent: "space-between" },
                    ]}
                  >
                    <Text style={[rightTextStyle, styles.weightLabel]}>NW</Text>
                    <Text
                      style={[rightTextStyle, styles.weightValueSilver]}
                    >
                      {formatWeight(productData.NWt)}
                    </Text>
                  </View>
                  <View
                    style={[
                      styles.weightRow,
                      { width: 38, justifyContent: "space-between" },
                    ]}
                  >
                    <Text
                      style={[
                        rightTextStyle,
                        styles.weightLabel,
                        { width: 20 },
                      ]}
                    >
                      {productData?.supplier_short_code || ""}
                    </Text>
                    <Text
                      style={[rightTextStyle, styles.weightValueSilver]}
                    >
                      {productData?.size ?? ""}
                    </Text>
                  </View>
                </View>
              )}
            </>
          ) : null}
        </View>
      </View>
    );
  };

  return (
    <Document>
      {pages.map((pageItems, pageIndex) => (
        <Page key={`barcode-page-${pageIndex}`} size="A4" style={pageStyle}>
          <View style={styles.container}>
            <View style={gridStyle}>
              {pageItems.map((item, index) =>
                renderSquare(item, index, pageIndex),
              )}
            </View>
          </View>
        </Page>
      ))}
    </Document>
  );
};

export default BarcodePDF;
